import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import Player from '@vimeo/player';
import { parseVideoUrl } from '../../utils/videoEmbed';
import DataTable from '@components/DataTable';
import { Dialog, DialogContent, DialogTitle } from '@components/ui/dialog';
import {
  Item,
  ItemGroup,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemDescription,
} from '@components/ui/item';
import {
  logoutCustomer,
  PortalSubscription,
  getCancellationOffer,
  getCancellationFrequencyOptions,
  submitCancellationOfferResponse,
  type CancellationPageStyle,
  type CancellationRetentionPage,
  type PageViewEntry,
} from '@api/customer-portal-api';
import { parseApiError } from '@domain/errors';
import i18n from '@i18n/index';
import { apiErrorMessage } from '@i18n/apiError';
import { useAccount } from '@hooks/portal/useAccount';
import { useOrder, useOrders } from '@hooks/portal/useOrders';
import { useCancelSubscription, useSubscriptions } from '@hooks/portal/useSubscriptions';
import { useStoreDetails } from '@hooks/portal/useStoreDetails';
import {
  capturePortalEvent,
  getStoredPortalIdentity,
  registerPortalContext,
} from '@lib/monitoring';
import {
  CustomerPortalLogoSizeInput,
  getCustomerPortalLogoSizeStyles,
} from '@lib/customerPortalLogoSize';
import {
  getSubscriptionDisplayStatus,
  isEndedSubscriptionDisplayStatus,
} from './subscriptionStatus';
import SubStatusBadge from './SubStatusBadge';
import {
  fmtAmount,
  fmtDate,
  fmtSubscriptionFrequency,
  fmtSubscriptionFrequencyInterval,
  formatAddressLines,
  subLabel,
  titleCase,
} from './portalFormat';
import PortalBannerImage from './PortalBannerImage';
import SubscriptionDetailsPage from './SubscriptionDetailsPage';
import {
  classifyFrequencyAcceptError,
  confirmedNextOrderDate,
  formatFrequency,
  frequencyAcceptLabel,
  radioIndexForKey,
  selectedFrequencyOption,
  type FrequencyOptionRow,
} from './frequencyOffer';
import {
  classifyDiscountAcceptError,
  discountAcceptLabel,
  discountTermsFromResponse,
  discountTermsOf,
  formatDiscountDuration,
  isDiscountPage,
  nextChargeDiscount,
  recurringPriceDiscount,
  settleDiscountConflict,
  type DiscountTerms,
} from './discountOffer';
import {
  CHECK_FAILED,
  forwardCta,
  frequencyCheckFromResponse,
  initialFrequencyCheck,
  isFrequencyPage,
  lastBecauseSkipped,
  mergeReports,
  nextStep,
  offerPageOf,
  outcomePage,
  previousPage,
  skipPassed,
  stepOf,
  viewEntry,
  type FrequencyCheck,
  type RetentionOffer,
  type WalkState,
} from './retentionFlow';

interface CustomStyles {
  primaryBackgroundColor: string | null;
  secondaryBackgroundColor: string | null;
  primaryTextColor: string | null;
  secondaryTextColor: string | null;
  buttonBackgroundColor: string | null;
  buttonTextColor: string | null;
  logo: string | null;
  logoSize: CustomerPortalLogoSizeInput;
}

interface CustomerPortalProps {
  customStyles?: Partial<CustomStyles>;
  portalContainer?: Element | null;
}

type OrderStatusKey = 'pending' | 'complete' | 'failed';

const getOrderStatusKey = (s: string): OrderStatusKey | null => {
  const v = (s || '').toLowerCase();
  if (v === 'pending' || v === 'processing') return 'pending';
  if (v === 'complete' || v === 'completed' || v === 'paid' || v === 'fulfilled') return 'complete';
  if (v === 'failed' || v === 'error') return 'failed';
  return null;
};

const fmtOrderStatus = (s: string): string => {
  const key = getOrderStatusKey(s);
  if (key) return i18n.t(`orders.status.${key}`);
  return s ? titleCase(s) : '—';
};

interface OrderStatusCfg {
  color: string;
  dot?: string;
  spinner?: boolean;
}

const orderStatusCfg: Record<OrderStatusKey, OrderStatusCfg> = {
  pending: { color: '#D97706', spinner: true },
  complete: { color: '#2563EB', dot: '#2563EB' },
  failed: { color: '#DC2626', dot: '#DC2626' },
};

const FALLBACK_ORDER_STATUS_CFG: OrderStatusCfg = { color: '#6B7280', dot: '#6B7280' };

const getOrderStatusCfg = (s: string): OrderStatusCfg => {
  const key = getOrderStatusKey(s);
  return key ? orderStatusCfg[key] : FALLBACK_ORDER_STATUS_CFG;
};

const getSubscriptionEventProperties = (sub: PortalSubscription) => ({
  subscription_id: sub.id,
  subscription_status: sub.status,
});

const EMPTY_SUBSCRIPTIONS: PortalSubscription[] = [];

// "Size to fit" (contain) image sizing — a percentage of the image area.
// Mirrors the merchant editor's PreviewShell/InlineImage so the portal is 1:1.
const clampOfferImageSize = (n?: number) => {
  const v = Math.round(n ?? 100);
  return Math.min(100, Math.max(25, Number.isFinite(v) && v ? v : 100));
};

const RetentionBanner: React.FC<{
  src: string;
  fit?: 'cover' | 'contain';
  size?: number;
}> = ({ src, fit = 'cover', size }) =>
  fit === 'contain' ? (
    // "Size to fit": whole image at `size`% of the card width.
    <div className="mt-4 flex w-full justify-center bg-white">
      <img
        src={src}
        alt=""
        className="object-contain"
        style={{ width: `${clampOfferImageSize(size)}%`, height: 'auto' }}
      />
    </div>
  ) : (
    <img src={src} alt="" className="h-32 w-full object-cover" />
  );

const RetentionInlineImage: React.FC<{
  src: string;
  fit?: 'cover' | 'contain';
  size?: number;
}> = ({ src, fit = 'cover', size }) => (
  <div className="px-6 pb-1 pt-2">
    {fit === 'contain' ? (
      // "Size to fit": whole image at `size`% of the content width.
      <div className="flex w-full justify-center">
        <img
          src={src}
          alt=""
          className="rounded-[10px] object-contain"
          style={{ width: `${clampOfferImageSize(size)}%`, height: 'auto' }}
        />
      </div>
    ) : (
      <img src={src} alt="" className="max-h-48 w-full rounded-[10px] object-cover" />
    )}
  </div>
);

// Subscription product card shown on the retention pages (replaces the old one-line label).
// Renders the product image, recurring cost (with struck pre-discount price + a discount
// badge when discounted), and a "first payment … then …" line only when the initial charge
// differs from the recurring one (split/v2 pricing). Colors come from the resolved style so
// the merchant editor preview stays 1:1.
const RetentionProductCard: React.FC<{
  sub: PortalSubscription | null;
  rs: ResolvedRetentionStyle;
}> = ({ sub, rs }) => {
  const { t } = useTranslation();
  if (!sub) return null;
  const lineItem = sub.line_item;
  const productName = lineItem?.name ?? t('dashboard.subscriptionFallbackName');
  const productImage = lineItem?.product_image ?? null;
  // A retention discount on renewals replaces the subscribe-and-save comparison.
  const retention = recurringPriceDiscount(sub);
  const recurringCents = retention?.discountedCents ?? sub.amount;
  const originalCents = retention?.fullCents ?? sub.original_amount ?? 0;
  const hasDiscount = originalCents > recurringCents && originalCents > 0;
  const discountPercent = retention
    ? retention.terms.percentage
    : (lineItem?.subscription?.discount_percent ??
      (hasDiscount ? Math.round((1 - recurringCents / originalCents) * 100) : null));
  const frequencyLabel = fmtSubscriptionFrequencyInterval(sub);

  // Show a separate initial line only when split (v2) pricing makes the first charge differ.
  const recurringUnit = lineItem?.subscription?.recurring_price ?? null;
  const initialUnit = lineItem?.price ?? null;
  const quantity = lineItem?.quantity ?? 1;
  const hasDistinctInitial =
    recurringUnit != null && initialUnit != null && recurringUnit !== initialUnit;
  const initialTotalCents = initialUnit != null ? initialUnit * quantity : null;

  return (
    <div className="px-6 pt-2 pb-1">
      <div
        className="flex items-center gap-3 rounded-[12px] border p-3"
        style={{ backgroundColor: rs.cardBg, borderColor: rs.cardBorder }}
      >
        {productImage && (
          <img
            src={productImage}
            alt={productName}
            className="h-14 w-14 flex-shrink-0 rounded-[8px] object-cover"
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] primary-font-semibold" style={{ color: rs.cardName }}>
            {productName}
          </p>
          <div className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            <span className="text-[15px] primary-font-semibold" style={{ color: rs.cardPrice }}>
              {fmtAmount(recurringCents, sub.currency)}
            </span>
            <span className="text-[12px]" style={{ color: rs.cardMeta }}>
              / {frequencyLabel}
            </span>
            {hasDiscount && (
              <>
                <span className="text-[12px] line-through" style={{ color: rs.cardMeta }}>
                  {fmtAmount(originalCents, sub.currency)}
                </span>
                {discountPercent != null && discountPercent > 0 && (
                  <span
                    className="text-[12px] primary-font-semibold"
                    style={{ color: rs.cardDiscount }}
                  >
                    {t('dashboard.percentOff', { percent: discountPercent })}
                  </span>
                )}
              </>
            )}
          </div>
          {hasDistinctInitial && initialTotalCents != null && (
            <p className="mt-0.5 text-[12px]" style={{ color: rs.cardMeta }}>
              {t('retention.firstPaymentThen', {
                initial: fmtAmount(initialTotalCents, sub.currency),
                recurring: fmtAmount(recurringCents, sub.currency),
                frequency: frequencyLabel,
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Small speaker icons for the video mute/unmute control (mirrors the checkout VSL template).
const MutedSpeakerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
    <path d="M3.63 3.63a.996.996 0 000 1.41L7.29 8.7 7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91-.36.15-.58.53-.58.92 0 .72.73 1.18 1.39.91.8-.33 1.55-.77 2.22-1.31l1.34 1.34a.996.996 0 101.41-1.41L5.05 3.63c-.39-.39-1.02-.39-1.42 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85C17.18 6.54 19 9.06 19 12zm-8.71-6.29l-.17.17L12 7.76V6.41c0-.89-1.08-1.33-1.71-.7z" />
  </svg>
);
const UnmutedSpeakerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 7.97v8.05c1.48-.73 2.5-2.25 2.5-3.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

// Renders the merchant's YouTube/Vimeo video in the retention modal and attempts autoplay
// WITH sound. The modal opens off a user click ("Cancel subscription"), so unmuted autoplay
// frequently succeeds; when the browser blocks it (Safari/iOS) we fall back to muted playback
// and surface an unmute button. Mirrors the checkout VSL template's SDK-driven approach.
// Stretch an SDK-created iframe to fill its 16:9 wrapper.
const fillVideoIframe = (iframe?: HTMLIFrameElement | null) => {
  if (!iframe) return;
  iframe.style.position = 'absolute';
  iframe.style.inset = '0';
  iframe.style.width = '100%';
  iframe.style.height = '100%';
  iframe.style.border = '0';
};

const RetentionVideo: React.FC<{ url: string }> = ({ url }) => {
  const { t } = useTranslation();
  const parsed = parseVideoUrl(url);
  // The SDK builds + owns the iframe inside this wrapper. We mount it into a node React
  // does NOT track (created imperatively) so React never tries to reconcile the
  // SDK-managed iframe — binding the API to a React-rendered iframe instead leaves a
  // black frame (the nocookie postMessage handshake fails, so onReady never fires).
  const wrapperRef = useRef<HTMLDivElement>(null);
  const vimeoPlayerRef = useRef<Player | null>(null);
  // YouTube IFrame API is loaded at runtime (no npm dep), so it has no static type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const youtubePlayerRef = useRef<any>(null);
  const [isMuted, setIsMuted] = useState(false);

  const provider = parsed?.provider;
  const videoId = parsed?.id;

  useEffect(() => {
    if (!provider || !videoId || !wrapperRef.current) return;
    const wrapper = wrapperRef.current;
    let cancelled = false;

    // SDK-owned mount node (not React-managed). Removed by hand on cleanup.
    const mount = document.createElement('div');
    mount.style.position = 'absolute';
    mount.style.inset = '0';
    wrapper.appendChild(mount);

    if (provider === 'vimeo') {
      const player = new Player(mount, {
        id: Number(videoId),
        autoplay: true,
        muted: false,
        controls: false, // hide Vimeo's native chrome; only our volume toggle shows
        dnt: true,
        title: false,
        byline: false,
        portrait: false,
      });
      vimeoPlayerRef.current = player;
      player
        .ready()
        .then(() => fillVideoIframe(mount.querySelector('iframe')))
        .catch(() => undefined);
      // Try with sound first; fall back to muted playback if the browser blocks it.
      player.setVolume(1).catch(() => undefined);
      player
        .play()
        .then(() => {
          if (!cancelled) setIsMuted(false);
        })
        .catch(() => {
          if (cancelled) return;
          player.setMuted(true).catch(() => undefined);
          player.play().catch(() => undefined);
          setIsMuted(true);
        });
      return () => {
        cancelled = true;
        vimeoPlayerRef.current = null;
        player.destroy().catch(() => undefined);
        mount.remove();
      };
    }

    // YouTube: build the player via the API (so the messaging origin matches the
    // nocookie host) rather than binding to a pre-rendered iframe.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const globalWindow = window as any;
    const ytTarget = document.createElement('div');
    mount.appendChild(ytTarget);
    const buildYouTube = () => {
      if (cancelled || !globalWindow.YT?.Player) return;
      youtubePlayerRef.current = new globalWindow.YT.Player(ytTarget, {
        host: 'https://www.youtube-nocookie.com',
        videoId,
        width: '100%',
        height: '100%',
        // Hide native chrome (controls/scrubber/fullscreen/keyboard/annotations/logo);
        // only our custom volume toggle is shown.
        playerVars: {
          autoplay: 1,
          mute: 0,
          playsinline: 1,
          rel: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          modestbranding: 1,
        },
        events: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onReady: (event: any) => {
            fillVideoIframe(event.target.getIframe?.());
            event.target.unMute();
            event.target.playVideo();
            // If unmuted autoplay was blocked, retry muted so the video still plays.
            window.setTimeout(() => {
              if (cancelled) return;
              const player = youtubePlayerRef.current;
              const isPlaying = player?.getPlayerState?.() === 1; // 1 = playing
              if (!isPlaying) {
                player?.mute?.();
                player?.playVideo?.();
                setIsMuted(true);
              } else {
                setIsMuted(!!player?.isMuted?.());
              }
            }, 800);
          },
        },
      });
    };

    if (globalWindow.YT?.Player) {
      buildYouTube();
    } else {
      const previousReady = globalWindow.onYouTubeIframeAPIReady;
      globalWindow.onYouTubeIframeAPIReady = () => {
        if (typeof previousReady === 'function') previousReady();
        buildYouTube();
      };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(script);
      }
    }

    return () => {
      cancelled = true;
      const player = youtubePlayerRef.current;
      youtubePlayerRef.current = null;
      try {
        player?.destroy?.();
      } catch {
        /* noop */
      }
      mount.remove();
    };
  }, [provider, videoId]);

  if (!parsed) return null;

  const toggleMute = () => {
    const next = !isMuted;
    if (vimeoPlayerRef.current) {
      vimeoPlayerRef.current.setMuted(next).catch(() => undefined);
      if (!next) vimeoPlayerRef.current.setVolume(1).catch(() => undefined);
    }
    if (youtubePlayerRef.current) {
      if (next) youtubePlayerRef.current.mute?.();
      else youtubePlayerRef.current.unMute?.();
    }
    setIsMuted(next);
  };

  return (
    <div className="px-6 pb-1 pt-2">
      <div
        ref={wrapperRef}
        className="relative w-full overflow-hidden rounded-[10px] bg-black"
        style={{ paddingBottom: '56.25%' }}
      >
        <button
          type="button"
          onClick={toggleMute}
          aria-label={isMuted ? t('retention.unmuteVideo') : t('retention.muteVideo')}
          className="absolute left-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white backdrop-blur transition-colors hover:bg-black/80"
        >
          {isMuted ? <MutedSpeakerIcon /> : <UnmutedSpeakerIcon />}
        </button>
      </div>
    </div>
  );
};

// Resolve a retention page's colors/visibility: explicit per-page override → store
// portal theme → built-in default. Unset fields all fall back to the same theme, so
// pages stay consistent and on-brand until a merchant customizes one.
interface ResolvedRetentionStyle {
  bg: string;
  headline: string;
  subtext: string;
  subLabel: string;
  step: string;
  primaryBg: string;
  primaryTxt: string;
  secondaryBg: string;
  secondaryTxt: string;
  accent: string;
  cancelBg: string;
  cancelTxt: string;
  headlineAlign: 'left' | 'center' | 'right';
  bodyAlign: 'left' | 'center' | 'right';
  showSubLabel: boolean;
  showStep: boolean;
  showBody: boolean;
  // Subscription product card
  cardPosition: 'above_headline' | 'below_body';
  cardName: string;
  cardPrice: string;
  cardDiscount: string;
  cardMeta: string;
  cardBg: string;
  cardBorder: string;
}
const pickColor = (override?: string | null, theme?: string | null, builtin?: string): string =>
  override ?? theme ?? builtin ?? '';
const resolveRetentionStyle = (
  s: CancellationPageStyle | undefined,
  theme: Partial<CustomStyles>,
): ResolvedRetentionStyle => {
  const st = s ?? {};
  return {
    bg: pickColor(st.background_color, theme.primaryBackgroundColor, '#FFFFFF'),
    headline: pickColor(st.headline_color, theme.primaryTextColor, '#111827'),
    subtext: pickColor(st.body_color, theme.secondaryTextColor, '#6B7280'),
    // Sub-label + step default to the body color, then theme secondary, then built-in.
    subLabel: pickColor(
      st.subscription_label_color,
      st.body_color ?? theme.secondaryTextColor,
      '#6B7280',
    ),
    step: pickColor(st.step_indicator_color, st.body_color ?? theme.secondaryTextColor, '#6B7280'),
    primaryBg: pickColor(st.primary_button_color, theme.buttonBackgroundColor, '#111827'),
    primaryTxt: pickColor(st.primary_button_text_color, theme.buttonTextColor, '#FFFFFF'),
    secondaryBg: pickColor(st.secondary_button_color, null, '#F5F5F5'),
    secondaryTxt: pickColor(st.secondary_button_text_color, null, '#374151'),
    accent: pickColor(st.accent_color, theme.buttonBackgroundColor, '#111827'),
    cancelBg: pickColor(st.cancel_button_color, null, '#E15C5C'),
    cancelTxt: pickColor(st.cancel_button_text_color, null, '#FFFFFF'),
    headlineAlign: st.headline_align ?? 'left',
    bodyAlign: st.body_align ?? 'left',
    showSubLabel: st.show_subscription_label !== false,
    showStep: st.show_step_indicator !== false,
    showBody: st.show_body !== false,
    cardPosition: st.product_card_position === 'below_body' ? 'below_body' : 'above_headline',
    cardName: pickColor(st.product_card_name_color, theme.primaryTextColor, '#111827'),
    cardPrice: pickColor(st.product_card_price_color, theme.primaryTextColor, '#111827'),
    cardDiscount: pickColor(st.product_card_discount_color, theme.buttonBackgroundColor, '#059669'),
    cardMeta: pickColor(st.product_card_meta_color, theme.secondaryTextColor, '#6B7280'),
    cardBg: pickColor(st.product_card_bg_color, null, '#F9FAFB'),
    cardBorder: pickColor(st.product_card_border_color, null, '#E5E7EB'),
  };
};

// Resolve a retention page's two image slots: banner (top) + body (inline).
interface RetentionImageSource {
  banner_image_url?: string | null;
  image_fit?: 'cover' | 'contain';
  image_size?: number;
  body_image_url?: string | null;
  body_image_fit?: 'cover' | 'contain';
  body_image_size?: number;
}
interface ResolvedRetentionImages {
  bannerUrl: string | null;
  bannerFit?: 'cover' | 'contain';
  bannerSize?: number;
  bodyUrl: string | null;
  bodyFit?: 'cover' | 'contain';
  bodySize?: number;
}
const resolveRetentionImages = (page?: RetentionImageSource | null): ResolvedRetentionImages => ({
  bannerUrl: page?.banner_image_url ?? null,
  bannerFit: page?.image_fit,
  bannerSize: page?.image_size,
  bodyUrl: page?.body_image_url ?? null,
  bodyFit: page?.body_image_fit,
  bodySize: page?.body_image_size,
});

const CustomerPortalDashboard: React.FC<CustomerPortalProps> = ({
  customStyles,
  portalContainer: externalPortalContainer,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscriptionId } = useParams();
  // The shell stays mounted across the main and details routes and the page scrolls on window,
  // so a new page would otherwise open at the old scroll position (mid-page on phones).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [subscriptionId]);
  // VITE_PORTAL_DOMAIN lets local dev resolve a real store while running on localhost.
  // Unset in prod/CI, so behavior is identical to window.location.hostname there.
  const domain =
    import.meta.env.VITE_PORTAL_DOMAIN ||
    (typeof window !== 'undefined' ? window.location.hostname : '');
  const storeQuery = useStoreDetails(domain);
  const content = storeQuery.data?.content;
  const fetchedStyles = storeQuery.data?.styles ?? {};
  const styles: Partial<CustomStyles> = { ...fetchedStyles, ...customStyles };
  const bg1 = styles.primaryBackgroundColor ?? '#FFFFFF';
  const txt1 = styles.primaryTextColor ?? '#111827';
  const txt2 = styles.secondaryTextColor ?? '#6B7280';
  const logo = styles.logo ?? null;
  const logoSize = styles.logoSize ?? 50;

  const subscriptionsQuery = useSubscriptions();
  const subscriptions = subscriptionsQuery.data ?? EMPTY_SUBSCRIPTIONS;
  const subsLoading = subscriptionsQuery.isPending;
  const subsError = subscriptionsQuery.isError ? apiErrorMessage(subscriptionsQuery.error) : null;

  const accountQuery = useAccount();
  const account = accountQuery.data ?? null;
  const accountLoading = accountQuery.isPending;
  const accountError = accountQuery.isError ? apiErrorMessage(accountQuery.error) : null;

  const accountDisplayName = account?.name?.trim() || account?.email || '';
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logoutCustomer();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const dashboardViewedRef = useRef(false);

  useEffect(() => {
    if (dashboardViewedRef.current || !accountQuery.isSuccess || !subscriptionsQuery.isSuccess) {
      return;
    }

    const { customerId, storeId: storedStoreId } = getStoredPortalIdentity();
    const storeId = storeQuery.data?.styles?.id ?? storedStoreId;
    const activeSubscriptionCount = subscriptions.filter((sub) => sub.status === 'active').length;

    registerPortalContext({
      customer_id: customerId,
      store_id: storeId,
    });
    capturePortalEvent('portal_dashboard_viewed', {
      customer_id: customerId,
      store_id: storeId,
      subscription_count: subscriptions.length,
      active_subscription_count: activeSubscriptionCount,
    });
    dashboardViewedRef.current = true;
  }, [
    accountQuery.isSuccess,
    storeQuery.data?.styles?.id,
    subscriptions,
    subscriptionsQuery.isSuccess,
  ]);

  const [cancelSub, setCancelSub] = useState<PortalSubscription | null>(null);
  const [cancelErr, setCancelErr] = useState<string | null>(null);
  const cancelMutation = useCancelSubscription();
  const canceling = cancelMutation.isPending;

  // ── Cancellation retention flow (paginated modal: before-you-go, offer page, reason) ──
  // The walk rules (pages shown, skips, step count) are pure functions in
  // ./retentionFlow; this component keeps the state and renders the current page.
  const [retentionSub, setRetentionSub] = useState<PortalSubscription | null>(null);
  const [retentionOffer, setRetentionOffer] = useState<RetentionOffer | null>(null);
  // The page on screen. null while the flow is open = waiting for the first page's
  // eligibility check (a change-frequency page comes first).
  const [currentRetentionPage, setCurrentRetentionPage] =
    useState<CancellationRetentionPage | null>(null);
  const [retentionReason, setRetentionReason] = useState<string | null>(null); // label text
  const [retentionBusy, setRetentionBusy] = useState(false);
  const [retentionErr, setRetentionErr] = useState<string | null>(null);
  // Change-frequency offer page: the eligibility check (one per flow, started on open),
  // the chosen schedule, whether retentionErr came from its accept (drives "Try again"),
  // the 409 state (schedules and accept hidden), and the "confirming" view shown when the
  // change may or may not have been applied (503, 502, 504 or no response).
  const [frequencyCheck, setFrequencyCheck] = useState<FrequencyCheck | null>(null);
  const [frequencyOptionId, setFrequencyOptionId] = useState<string | null>(null);
  const [frequencyAcceptFailed, setFrequencyAcceptFailed] = useState(false);
  const [frequencyUnavailable, setFrequencyUnavailable] = useState(false);
  const [frequencyPending, setFrequencyPending] = useState(false);
  // The schedule a confirmed switch moved to: the success view shows it until Done.
  const [frequencyChanged, setFrequencyChanged] = useState<FrequencyOptionRow | null>(null);
  // Discount offer page: the 409 state (accept hidden, message kept after Back), whether
  // retentionErr is the unconfirmed (502) message (drives "Try again"), and the terms Whop
  // confirmed: the success view shows them until Done.
  const [discountUnavailable, setDiscountUnavailable] = useState(false);
  const [discountUnconfirmed, setDiscountUnconfirmed] = useState(false);
  const [discountApplied, setDiscountApplied] = useState<DiscountTerms | null>(null);
  // Views and skip reports this interaction (per-page funnel) + whether they resolved
  // (kept/paused/changed/cancelled). On close without a resolve we report an "abandoned"
  // outcome so their views and skips still count.
  const retentionReportsRef = useRef<PageViewEntry[]>([]);
  const retentionResolvedRef = useRef(false);
  // An accept in this flow was unconfirmed (502): a later 409 may mean it went through.
  const discountUncertainRef = useRef(false);
  // Bumped whenever a flow opens or closes, so a late eligibility answer from an earlier
  // flow is ignored.
  const retentionFlowIdRef = useRef(0);

  const addRetentionReports = (...entries: Array<PageViewEntry | null>) => {
    retentionReportsRef.current = mergeReports(
      retentionReportsRef.current,
      entries.filter((entry): entry is PageViewEntry => entry !== null),
    );
  };

  // Clear the flow without reporting anything.
  const resetRetention = () => {
    retentionFlowIdRef.current += 1;
    setRetentionSub(null);
    setRetentionOffer(null);
    setCurrentRetentionPage(null);
    setRetentionReason(null);
    setRetentionErr(null);
    setFrequencyCheck(null);
    setFrequencyOptionId(null);
    setFrequencyAcceptFailed(false);
    setFrequencyUnavailable(false);
    setFrequencyPending(false);
    setFrequencyChanged(null);
    setDiscountUnavailable(false);
    setDiscountUnconfirmed(false);
    setDiscountApplied(null);
    discountUncertainRef.current = false;
  };

  const closeRetention = () => {
    if (retentionBusy) return;
    // Closed without keeping/cancelling → record the journey as abandoned (views only).
    if (!retentionResolvedRef.current && retentionSub && retentionOffer) {
      void submitCancellationOfferResponse(retentionSub.id, {
        cancellation_offer_id: retentionOffer.id,
        outcome: 'abandoned',
        pages_viewed: [...retentionReportsRef.current],
      }).catch(() => undefined);
    }
    // A schedule change may have gone through ("confirming" view): refresh the card.
    if (frequencyPending) void subscriptionsQuery.refetch();
    resetRetention();
  };

  const walkState: WalkState = { check: frequencyCheck };
  const retentionOfferPage = offerPageOf(retentionOffer);
  const isFrequencyOffer = isFrequencyPage(retentionOfferPage);
  const frequencyData = frequencyCheck?.status === 'eligible' ? frequencyCheck.data : null;
  const selectedFrequency = selectedFrequencyOption(frequencyData, frequencyOptionId);
  const isDiscountOffer = isDiscountPage(retentionOfferPage);
  const discountTerms = discountTermsOf(retentionOfferPage);
  const retentionStep = retentionOffer
    ? stepOf(retentionOffer, walkState, currentRetentionPage)
    : null;
  const retentionPreviousPage =
    retentionOffer && currentRetentionPage
      ? previousPage(retentionOffer, walkState, currentRetentionPage)
      : null;
  const bygCta = retentionOffer
    ? forwardCta(retentionOffer, walkState, 'before_you_go')
    : 'continue';
  // The red last-step CTA on Before you go only when a skipped change-frequency page after
  // it made it the last page; otherwise today's neutral Continue.
  const bygLastStepCancel =
    !!retentionOffer && lastBecauseSkipped(retentionOffer, walkState, 'before_you_go');
  const reasonCta = retentionOffer
    ? forwardCta(retentionOffer, walkState, 'cancellation_reason')
    : 'continue';
  // The confirming or success view replaces the offer page after Switch my schedule, and
  // the success view after an accepted discount.
  const offerResultView = frequencyPending || frequencyChanged !== null || discountApplied !== null;
  // Focus its heading so screen readers read the result out (focus would stay on the dialog).
  const offerResultHeadingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (offerResultView) offerResultHeadingRef.current?.focus();
  }, [offerResultView]);
  // Pause pages show their own texts as before; change-frequency pages fall back to the
  // default copy (the backend serves them even with an empty headline).
  const offerHeadline = isFrequencyOffer
    ? retentionOfferPage?.headline || t('retention.frequency.defaultHeadline')
    : retentionOfferPage?.headline;
  const offerBody = isFrequencyOffer
    ? retentionOfferPage?.body || t('retention.frequency.defaultBody')
    : retentionOfferPage?.body;

  // Per-page resolved colors/visibility (merchant override → store theme → built-in).
  const bygStyle = resolveRetentionStyle(retentionOffer?.before_you_go?.style, styles);
  const offerStyle = resolveRetentionStyle(retentionOfferPage?.style, styles);
  const reasonStyle = resolveRetentionStyle(retentionOffer?.cancellation_reason?.style, styles);
  const bygImages = resolveRetentionImages(retentionOffer?.before_you_go);
  const offerImages = resolveRetentionImages(retentionOfferPage);
  const reasonImages = resolveRetentionImages(retentionOffer?.cancellation_reason);

  // Track which pages the customer actually lands on (for the funnel + abandon report).
  useEffect(() => {
    if (retentionOffer && currentRetentionPage) {
      retentionReportsRef.current = mergeReports(retentionReportsRef.current, [
        viewEntry(retentionOffer, currentRetentionPage),
      ]);
    }
  }, [currentRetentionPage, retentionOffer]);

  // Start the walk at the first page that will be shown. Returns 'wait' while that is a
  // change-frequency page whose eligibility check is still running ("Checking options…").
  // Uses only setters and refs, so it is safe to call from the check's late callback.
  const beginRetentionWalk = (
    sub: PortalSubscription,
    offer: RetentionOffer,
    check: FrequencyCheck | null,
  ): 'wait' | 'started' => {
    const state: WalkState = { check };
    const step = nextStep(offer, state, null);
    if (step.kind === 'wait') return 'wait';
    addRetentionReports(skipPassed(offer, state, null, step.kind === 'page' ? step.page : null));
    if (step.kind === 'page') {
      setCurrentRetentionPage(step.page);
      capturePortalEvent('portal_cancellation_offer_shown', getSubscriptionEventProperties(sub));
      return 'started';
    }
    // Every page was skipped: report the skip, then fall back to the plain cancel confirm.
    retentionResolvedRef.current = true;
    void submitCancellationOfferResponse(sub.id, {
      cancellation_offer_id: offer.id,
      outcome: 'abandoned',
      pages_viewed: [...retentionReportsRef.current],
    }).catch(() => undefined);
    resetRetention();
    setCancelSub(sub);
    return 'started';
  };

  const openCancel = async (sub: PortalSubscription) => {
    setCancelErr(null);
    // Try to surface a retention flow before the plain confirm modal.
    try {
      const { offer } = await getCancellationOffer(sub.id);
      const offerPage = offerPageOf(offer);
      if (offer && (offer.before_you_go || offerPage || offer.cancellation_reason)) {
        // Fresh interaction: reset the flow, view tracking and the resolved flag.
        resetRetention();
        const flowId = retentionFlowIdRef.current;
        retentionReportsRef.current = [];
        retentionResolvedRef.current = false;
        const check = initialFrequencyCheck(offerPage);
        setRetentionOffer(offer);
        setRetentionSub(sub);
        setFrequencyCheck(check);
        const start = beginRetentionWalk(sub, offer, check);
        if (check?.status === 'checking' && offerPage?.offer_page_id) {
          // One eligibility check per flow, started now so reaching the page rarely waits
          // on Whop. An error counts as a skip: check_failed.
          getCancellationFrequencyOptions(sub.id, {
            cancellationOfferId: offer.id,
            offerPageId: offerPage.offer_page_id,
          })
            .then(frequencyCheckFromResponse)
            // A non-2xx, no answer, or a 200 body the mapper cannot read: check_failed,
            // so the walk never stays on "Checking options…".
            .catch((): FrequencyCheck => ({ status: 'skipped', reason: CHECK_FAILED }))
            .then((result) => {
              if (retentionFlowIdRef.current !== flowId) return; // flow closed or reopened
              setFrequencyCheck(result);
              if (start === 'wait') beginRetentionWalk(sub, offer, result);
            });
        }
        return;
      }
    } catch {
      // If the lookup fails, fall through to the normal cancel confirm.
    }
    setCancelSub(sub);
  };

  // Customer chose to keep the subscription from within the retention flow.
  const retentionKeep = async () => {
    if (!retentionSub || !retentionOffer) return;
    retentionResolvedRef.current = true;
    setRetentionBusy(true);
    try {
      await submitCancellationOfferResponse(retentionSub.id, {
        cancellation_offer_id: retentionOffer.id,
        outcome: 'retained_no_cancel',
        ...outcomePage(retentionOffer, currentRetentionPage),
        pages_viewed: [...retentionReportsRef.current],
      });
      capturePortalEvent(
        'portal_cancellation_offer_retained',
        getSubscriptionEventProperties(retentionSub),
      );
    } catch {
      // Non-fatal: keeping the subscription requires no further action.
    } finally {
      setRetentionBusy(false);
      closeRetention();
    }
  };

  // Customer advances past the last page toward cancellation.
  // The retention flow IS the cancel confirmation — submitting "proceeded_to_cancel"
  // records the reason AND finalizes the cancellation (backend shared helper). No
  // separate "Cancel subscription?" modal when a flow is configured.
  const retentionProceedToCancel = async () => {
    if (!retentionSub || !retentionOffer) return;
    const sub = retentionSub;
    // Mark resolved BEFORE the request so closeRetention() doesn't also fire a stray
    // "abandoned" row that races this cancel in the per-(sub, period) dedup.
    retentionResolvedRef.current = true;
    setRetentionBusy(true);
    setRetentionErr(null);
    setFrequencyAcceptFailed(false);
    setDiscountUnconfirmed(false);
    try {
      await submitCancellationOfferResponse(sub.id, {
        cancellation_offer_id: retentionOffer.id,
        selected_reason: retentionReason,
        outcome: 'proceeded_to_cancel',
        // Attribute the cancel to the page it happened on + record every page seen, so
        // the per-page funnel (incl. the Offer card) reflects decline-then-cancel.
        ...outcomePage(retentionOffer, currentRetentionPage),
        pages_viewed: [...retentionReportsRef.current],
      });
      capturePortalEvent('portal_cancellation_offer_reason_selected', {
        ...getSubscriptionEventProperties(sub),
        cancellation_reason: retentionReason,
      });
      capturePortalEvent('portal_subscription_canceled', getSubscriptionEventProperties(sub));
      await subscriptionsQuery.refetch();
      setRetentionBusy(false);
      closeRetention();
    } catch (err) {
      // Failed cancel → reopen for retry and let a later close report abandoned views.
      retentionResolvedRef.current = false;
      setRetentionBusy(false);
      setRetentionErr(apiErrorMessage(err));
    }
  };

  // Customer accepted the pause offer — pause the subscription instead of cancelling.
  const retentionAcceptPause = async () => {
    if (!retentionSub || !retentionOffer) return;
    const sub = retentionSub;
    retentionResolvedRef.current = true;
    setRetentionBusy(true);
    setRetentionErr(null);
    try {
      await submitCancellationOfferResponse(sub.id, {
        cancellation_offer_id: retentionOffer.id,
        outcome: 'accepted_pause',
        ...outcomePage(retentionOffer, 'offer'),
        pages_viewed: [...retentionReportsRef.current],
      });
      capturePortalEvent('portal_cancellation_offer_paused', getSubscriptionEventProperties(sub));
      await subscriptionsQuery.refetch();
      setRetentionBusy(false);
      closeRetention();
    } catch (err) {
      // Pause failed → let them retry or decline; keep the abandon report accurate.
      retentionResolvedRef.current = false;
      setRetentionBusy(false);
      setRetentionErr(apiErrorMessage(err));
    }
  };

  // Customer accepted a longer delivery schedule instead of cancelling.
  // Never cancels: a failure keeps them on the page, and an unconfirmed outcome
  // (503/502/504/no response) shows the "confirming" view.
  const retentionAcceptFrequency = async () => {
    const offerPageId = retentionOfferPage?.offer_page_id;
    const option = selectedFrequency;
    if (!retentionSub || !retentionOffer || !offerPageId || !option) return;
    const sub = retentionSub;
    retentionResolvedRef.current = true;
    setRetentionBusy(true);
    setRetentionErr(null);
    setFrequencyAcceptFailed(false);
    try {
      const response = await submitCancellationOfferResponse(sub.id, {
        cancellation_offer_id: retentionOffer.id,
        outcome: 'accepted_change_frequency',
        page: 'offer',
        offer_page_id: offerPageId,
        selected_reason: retentionReason,
        frequency_option_id: option.option_id,
        billing_period_days: option.billing_period_days,
        pages_viewed: [...retentionReportsRef.current],
      });
      capturePortalEvent('portal_cancellation_offer_frequency_changed', {
        ...getSubscriptionEventProperties(sub),
        cancellation_reason: retentionReason,
        frequency_value: option.frequency_value,
        frequency_unit: option.frequency_unit,
      });
      await subscriptionsQuery.refetch();
      setRetentionBusy(false);
      setFrequencyChanged({
        ...option,
        next_payment_date: confirmedNextOrderDate(option, response),
      });
    } catch (err) {
      setRetentionBusy(false);
      const failure = classifyFrequencyAcceptError(err);
      // 'pending' = not confirmed yet (the switch may still have happened).
      capturePortalEvent('portal_cancellation_offer_frequency_change_failed', {
        ...getSubscriptionEventProperties(sub),
        failure,
        error_category: parseApiError(err).code,
      });
      if (failure === 'pending') {
        // May have been applied: stay resolved so closing doesn't report "abandoned".
        setFrequencyPending(true);
        return;
      }
      // Definite failure → let them retry or decline; keep the abandon report accurate.
      retentionResolvedRef.current = false;
      if (failure === 'unavailable') {
        // Shown from frequencyUnavailable, so the message stays after Back and Decline.
        setFrequencyUnavailable(true);
        return;
      }
      setRetentionErr(t('retention.frequency.errors.failed'));
      setFrequencyAcceptFailed(true);
    }
  };

  // Customer accepted a discount on future renewals instead of cancelling. The backend
  // applies it on Whop; the subscription stays active and its next payment date is
  // unchanged. Never cancels: a 409 shows a short message and moves on to the next page,
  // and an unconfirmed outcome (502) offers "Try again", which re-sends the same request.
  const retentionAcceptDiscount = async () => {
    const terms = discountTerms;
    if (!retentionSub || !retentionOffer || !terms) return;
    const sub = retentionSub;
    const trackApplied = (confirmed: DiscountTerms) =>
      capturePortalEvent('portal_cancellation_offer_discounted', {
        ...getSubscriptionEventProperties(sub),
        cancellation_reason: retentionReason,
        discount_percentage: confirmed.percentage,
        discount_duration: confirmed.duration,
        discount_duration_months: confirmed.months,
      });
    retentionResolvedRef.current = true;
    setRetentionBusy(true);
    setRetentionErr(null);
    setDiscountUnconfirmed(false);
    try {
      const response = await submitCancellationOfferResponse(sub.id, {
        cancellation_offer_id: retentionOffer.id,
        outcome: 'accepted_discount',
        ...outcomePage(retentionOffer, 'offer'),
        selected_reason: retentionReason,
        pages_viewed: [...retentionReportsRef.current],
      });
      const confirmed = discountTermsFromResponse(terms, response);
      trackApplied(confirmed);
      await subscriptionsQuery.refetch();
      setRetentionBusy(false);
      setDiscountApplied(confirmed);
    } catch (err) {
      let failure = classifyDiscountAcceptError(err);
      if (failure === 'unavailable' && discountUncertainRef.current) {
        // The unconfirmed attempt may have applied it after all: the refreshed subscription
        // decides between the success view, "unavailable" and staying unconfirmed.
        const refresh = await subscriptionsQuery.refetch();
        const settled = settleDiscountConflict({
          failed: refresh.isError,
          sub: refresh.data?.find((s) => s.id === sub.id),
        });
        if (settled.kind === 'applied') {
          trackApplied(settled.terms);
          setRetentionBusy(false);
          setDiscountApplied(settled.terms);
          return;
        }
        failure = settled.kind;
      }
      // Not applied (or not confirmed) → they can retry or decline; keep the abandon
      // report accurate.
      retentionResolvedRef.current = false;
      setRetentionBusy(false);
      capturePortalEvent('portal_cancellation_offer_discount_failed', {
        ...getSubscriptionEventProperties(sub),
        failure,
        error_category: parseApiError(err).code,
      });
      if (failure === 'unavailable') {
        // Shown from discountUnavailable, so the message stays on the next page and after
        // Back. When the offer page is the last page, stay on it: Decline still cancels.
        setDiscountUnavailable(true);
        const step = nextStep(retentionOffer, walkState, 'offer');
        if (step.kind === 'page') {
          addRetentionReports(skipPassed(retentionOffer, walkState, 'offer', step.page));
          setCurrentRetentionPage(step.page);
        }
        return;
      }
      if (failure === 'unconfirmed') {
        discountUncertainRef.current = true;
        setRetentionErr(t('retention.discount.errors.unconfirmed'));
        setDiscountUnconfirmed(true);
        return;
      }
      setRetentionErr(apiErrorMessage(err));
    }
  };

  // Continue / Decline: go to the next page that will be shown, reporting a skipped page
  // passed on the way; with no page left, the flow IS the cancel confirmation.
  const retentionNext = () => {
    if (!retentionOffer) return;
    const step = nextStep(retentionOffer, walkState, currentRetentionPage);
    if (step.kind === 'wait') return; // the button shows "Checking options…"
    addRetentionReports(
      skipPassed(
        retentionOffer,
        walkState,
        currentRetentionPage,
        step.kind === 'page' ? step.page : null,
      ),
    );
    if (step.kind === 'end') {
      retentionProceedToCancel();
      return;
    }
    setRetentionErr(null);
    setFrequencyAcceptFailed(false);
    setDiscountUnconfirmed(false);
    setCurrentRetentionPage(step.page);
  };

  const retentionBack = () => {
    if (!retentionPreviousPage) return;
    setRetentionErr(null);
    setFrequencyAcceptFailed(false);
    setDiscountUnconfirmed(false);
    setCurrentRetentionPage(retentionPreviousPage);
  };

  const closeCancel = () => {
    if (canceling) return;
    setCancelSub(null);
    setCancelErr(null);
  };

  const submitCancel = () => {
    if (!cancelSub) return;
    const cancellationMode = 'at_period_end';
    const cancelEventProperties = {
      ...getSubscriptionEventProperties(cancelSub),
      cancellation_mode: cancellationMode,
      cancel_at_period_end: cancellationMode === 'at_period_end',
    };
    setCancelErr(null);
    capturePortalEvent('portal_subscription_cancel_requested', cancelEventProperties);
    cancelMutation.mutate(
      { subscriptionId: cancelSub.id, cancellationMode },
      {
        onSuccess: () => {
          capturePortalEvent('portal_subscription_canceled', cancelEventProperties);
          setCancelSub(null);
        },
        onError: (err) => {
          capturePortalEvent('portal_subscription_cancel_failed', {
            ...cancelEventProperties,
            error_category: parseApiError(err).code,
          });
          setCancelErr(apiErrorMessage(err));
        },
      },
    );
  };

  const [page, setPage] = useState(1);
  const ordersPerPage = 10;
  const ordersQuery = useOrders({ page, pageSize: ordersPerPage });
  const orders = ordersQuery.data?.orders ?? [];
  const ordersTotal = ordersQuery.data?.total ?? 0;
  const ordersLoading = ordersQuery.isPending || ordersQuery.isFetching;
  const ordersError = ordersQuery.isError ? apiErrorMessage(ordersQuery.error) : null;
  const totalPages = Math.max(1, Math.ceil(ordersTotal / ordersPerPage));

  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const detailOrderQuery = useOrder(detailOrderId);
  const detailOrder = detailOrderQuery.data ?? null;
  const detailLoading = detailOrderQuery.isFetching;
  const detailError = detailOrderQuery.isError ? apiErrorMessage(detailOrderQuery.error) : null;

  const openOrderDetail = (orderId: string) => {
    setDetailOrderId(orderId);
  };

  const closeOrderDetail = () => {
    setDetailOrderId(null);
  };

  const changeOrdersPage = (nextPage: number) => {
    setPage(nextPage);
  };

  const upcomingSubs = subscriptions
    .filter((s) => s.status === 'active' && s.next_payment_date && !s.cancel_at_period_end)
    .sort(
      (a, b) =>
        new Date(a.next_payment_date as string).getTime() -
        new Date(b.next_payment_date as string).getTime(),
    );

  const fmtDateTime = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? '—'
      : d.toLocaleString(i18n.language, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
  };

  const portalContainer = externalPortalContainer ?? null;
  const detailsSubscription = subscriptionId
    ? (subscriptions.find((sub) => sub.id === subscriptionId) ?? null)
    : null;

  const orderTableColumns = [
    { id: 'order_id', label: t('orders.columns.orderId') },
    { id: 'amount', label: t('orders.columns.amount') },
    { id: 'status', label: t('orders.columns.status') },
    { id: 'date', label: t('orders.columns.date') },
  ];

  const orderTableRows = orders.map((order) => {
    const cfg = getOrderStatusCfg(order.status);
    return {
      id: order.id,
      order_id: <span className="font-medium text-[#111827]">{order.order_number}</span>,
      amount: (
        <span className="text-[#111827]">
          {fmtAmount(order.total_price, order.currency || 'usd')}
        </span>
      ),
      status: (
        <span className="inline-flex bg-white border border-[#E5E5E5] rounded-full px-2 items-center gap-1.5 text-[13px]">
          {cfg.spinner ? (
            <img src="/assets/images/icons/pending.png" alt="" className="w-3 h-3" />
          ) : (
            <span
              className="w-2 h-2 rounded-full inline-block flex-shrink-0"
              style={{ backgroundColor: cfg.dot }}
            />
          )}
          {fmtOrderStatus(order.status)}
        </span>
      ),
      date: <span className="text-[#111827] text-[12px]">{fmtDate(order.created_at)}</span>,
    };
  });

  return (
    <div
      style={{
        backgroundColor: bg1,
        color: txt1,
        minHeight: '100vh',
        fontFamily: 'Inter, sans-serif',
        position: 'relative',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
        <div className="flex flex-wrap justify-between items-center gap-3 mb-8">
          {logo ? (
            <img
              src={logo}
              alt={t('common.logoAlt')}
              style={{ ...getCustomerPortalLogoSizeStyles(logoSize), objectFit: 'contain' }}
            />
          ) : (
            <div className="w-10" />
          )}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {accountDisplayName && (
              <span
                className="text-[14px] font-medium px-4 py-2 rounded-[6px] border"
                style={{ borderColor: txt1, color: txt1 }}
              >
                {accountDisplayName}
              </span>
            )}
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="text-[14px] font-medium px-4 py-2 rounded-[6px] border transition-opacity hover:opacity-80 disabled:opacity-50"
              style={{ borderColor: '#E5E5E5', color: txt1, backgroundColor: '#FFFFFF' }}
            >
              {loggingOut ? t('dashboard.loggingOut') : t('dashboard.logout')}
            </button>
          </div>
        </div>

        {subscriptionId ? (
          subsLoading ? (
            <div className="flex justify-center items-center py-10">
              <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin" />
            </div>
          ) : subsError ? (
            <div className="text-center py-10 text-[14px] text-[#DC2626]">{subsError}</div>
          ) : detailsSubscription ? (
            <SubscriptionDetailsPage
              subscription={detailsSubscription}
              account={account}
              perks={content?.perks ?? null}
              txt1={txt1}
              txt2={txt2}
              portalContainer={portalContainer}
              onBack={() => navigate('/dashboard')}
              onCancel={openCancel}
            />
          ) : (
            <Navigate to="/dashboard" replace />
          )
        ) : (
          <>
            <PortalBannerImage banner={content?.topBanner ?? null} position="top" />

            <h2 className="primary-font-semibold text-[20px] mb-1" style={{ color: txt1 }}>
              {t('dashboard.title')}
            </h2>
            <p className="text-[15px] mb-5" style={{ color: txt2 }}>
              {t('dashboard.subtitle')}
            </p>

            {subsLoading ? (
              <div className="flex justify-center items-center py-10">
                <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin" />
              </div>
            ) : subsError ? (
              <div className="text-center py-10 text-[14px] text-[#DC2626]">{subsError}</div>
            ) : subscriptions.length === 0 ? (
              <div className="text-center py-10 text-[14px]" style={{ color: txt2 }}>
                {t('dashboard.noSubscriptions')}
              </div>
            ) : (
              <div
                className="flex flex-col sm:flex-row gap-4 mb-6 sm:overflow-x-auto pb-2"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                {subscriptions.map((sub) => {
                  const rawStatus = getSubscriptionDisplayStatus(sub.status, false);
                  const displayStatus = getSubscriptionDisplayStatus(
                    sub.status,
                    sub.cancel_at_period_end,
                  );
                  const isEnded = isEndedSubscriptionDisplayStatus(rawStatus);
                  const isInactive = isEnded || rawStatus === 'paused';
                  const li = sub.line_item;
                  const title = li?.name ?? t('dashboard.subscriptionFallbackName');
                  const image = li?.product_image ?? null;
                  const variantOptions = li?.selected_options ?? [];
                  const endedOn = sub.cancel_date ?? sub.end_date ?? null;
                  const isPaused = rawStatus === 'paused';
                  // A paused sub isn't cancelling — Whop flips cancel_at_period_end on pause, so
                  // paused takes precedence and we show when it resumes, not a cancel date.
                  const isCancelPending = sub.cancel_at_period_end && !isEnded && !isPaused;
                  // The next charge's price: a retention discount (live subscriptions only) shows
                  // the full price struck through; otherwise the subscribe-and-save original.
                  const retention = isEnded || isCancelPending ? null : nextChargeDiscount(sub);
                  const priceCents = retention?.discountedCents ?? sub.amount;
                  const strikeCents = retention?.fullCents ?? sub.original_amount;

                  return (
                    <Link
                      key={sub.id}
                      to={`/dashboard/subscriptions/${encodeURIComponent(sub.id)}`}
                      // The resting shadow is a class: an inline one would beat hover:shadow-md.
                      className="block no-underline w-full sm:w-80 sm:flex-shrink-0 rounded-[10px] p-5 shadow-[0px_1px_3px_0px_#0000001A] transition-shadow hover:shadow-md"
                      style={{
                        border: `1px solid ${isInactive ? 'transparent' : '#E5E5E5'}`,
                        backgroundColor: isInactive ? '#E5E5E5' : '#FFFFFF',
                      }}
                    >
                      <div className="flex justify-between items-start gap-2 mb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {image && (
                            <img
                              src={image}
                              alt={title}
                              className="w-12 h-12 rounded-[8px] object-cover flex-shrink-0"
                              style={{ border: '1px solid #E5E5E5', backgroundColor: '#FFFFFF' }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          <span
                            className="text-[13px] truncate primary-font-medium min-w-0"
                            style={{ color: txt1 }}
                          >
                            {title}
                          </span>
                        </div>
                        <SubStatusBadge status={displayStatus} />
                      </div>

                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-baseline gap-2 min-w-0">
                          <span className="primary-font-medium text-[24px]" style={{ color: txt1 }}>
                            {fmtAmount(priceCents, sub.currency)}
                          </span>
                          {strikeCents > priceCents && (
                            <span className="text-[12px] line-through" style={{ color: txt2 }}>
                              {fmtAmount(strikeCents, sub.currency)}
                            </span>
                          )}
                        </div>
                        <span
                          className="text-[12px] px-2 py-0.5 bg-[#F5F5F5] rounded-full border flex-shrink-0"
                          style={{ borderColor: '#e5e7eb', color: txt2 }}
                        >
                          {fmtSubscriptionFrequency(sub)}
                        </span>
                      </div>

                      {retention && (
                        <div className="mb-3">
                          <span
                            className="inline-flex text-[12px] px-2 py-0.5 rounded-full border primary-font-medium"
                            style={{
                              borderColor: '#BBF7D0',
                              backgroundColor: '#F0FDF4',
                              color: '#15803D',
                            }}
                          >
                            {t('retention.discount.summary', {
                              percent: retention.terms.percentage,
                              duration: formatDiscountDuration(t, retention.terms),
                            })}
                          </span>
                        </div>
                      )}

                      {variantOptions.length > 0 && (
                        <div
                          className="flex flex-wrap items-center gap-2 text-[12px] mb-3"
                          style={{ color: txt2 }}
                        >
                          {variantOptions.map(({ name, value }, i) => (
                            <span
                              key={`${name}-${i}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border rounded"
                              style={{ borderColor: '#E5E5E5', color: txt1 }}
                            >
                              <span style={{ color: txt2 }}>{name}:</span>
                              <span className="primary-font-medium">{value}</span>
                            </span>
                          ))}
                        </div>
                      )}

                      {li?.quantity && li.quantity > 1 ? (
                        <div className="text-[12px] mb-1" style={{ color: txt2 }}>
                          {t('dashboard.card.quantity')}{' '}
                          <span style={{ color: txt1 }}>{li.quantity}</span>
                        </div>
                      ) : null}

                      {sub.start_date && (
                        <div className="text-[13px]" style={{ color: txt2 }}>
                          <span className="font-medium">{t('dashboard.card.started')}</span>
                          <span>{fmtDate(sub.start_date)}</span>
                        </div>
                      )}

                      {!isEnded ? (
                        <div className="text-[13px] mb-4" style={{ color: txt2 }}>
                          {isPaused ? (
                            sub.pause_resumes_at ? (
                              <>
                                <span className="font-medium">{t('dashboard.card.resumesOn')}</span>
                                <span>{fmtDate(sub.pause_resumes_at)}</span>
                              </>
                            ) : (
                              // Merchant "pause for now" has no scheduled resume date — it's resumed
                              // manually on our end, so avoid implying a date exists.
                              <span className="font-medium">
                                {t('dashboard.card.resumesWhenReactivated')}
                              </span>
                            )
                          ) : isCancelPending ? (
                            <>
                              <span className="font-medium">{t('dashboard.card.cancelsOn')}</span>
                              <span>{fmtDate(sub.next_payment_date)}</span>
                            </>
                          ) : (
                            <>
                              <span className="font-medium">{t('dashboard.card.nextOrder')}</span>
                              <span>{fmtDate(sub.next_payment_date)}</span>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="text-[13px] mb-4" style={{ color: txt2 }}>
                          <span className="font-medium">
                            {rawStatus === 'expired'
                              ? t('dashboard.card.expired')
                              : t('dashboard.card.ended')}
                            {endedOn ? `: ${fmtDate(endedOn)}` : ''}
                          </span>
                        </div>
                      )}

                      <div
                        className="flex items-center justify-between border-t pt-3 text-[13px] primary-font-medium"
                        style={{ borderColor: '#E5E5E5', color: txt1 }}
                      >
                        <span>{t('dashboard.card.viewDetails')}</span>
                        <span aria-hidden="true">›</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {upcomingSubs.length > 0 && (
              <>
                <h2 className="primary-font-semibold text-[20px] mb-1" style={{ color: txt1 }}>
                  {t('dashboard.upcoming.title')}
                </h2>
                <p className="text-[15px] mb-4" style={{ color: txt2 }}>
                  {t('dashboard.upcoming.subtitle')}
                </p>
              </>
            )}

            {upcomingSubs.map((sub) => {
              const li = sub.line_item;
              // Only the next order is shown here, and that is the charge the note covers.
              const retention = nextChargeDiscount(sub);
              return (
                <div
                  key={sub.id}
                  className="rounded-[10px] p-5 mb-6"
                  style={{
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    boxShadow: '0px 1px 3px 0px #0000001A',
                  }}
                >
                  <h3 className="primary-font-semibold text-[18px] mb-0.5" style={{ color: txt1 }}>
                    {t('dashboard.upcoming.nextOrder')}
                  </h3>
                  <p className="text-[13px] mb-4" style={{ color: txt2 }}>
                    {t('dashboard.upcoming.orderDate')}&nbsp;&nbsp;{fmtDate(sub.next_payment_date)}
                  </p>

                  <div className="flex gap-5 flex-wrap">
                    <div className="flex-1" style={{ minWidth: 260 }}>
                      {li && (
                        <ItemGroup className="gap-2 mb-3">
                          <Item variant="outline">
                            {li.product_image && (
                              <ItemMedia variant="image">
                                <img
                                  src={li.product_image}
                                  alt={li.name}
                                  className="w-full h-full object-cover"
                                />
                              </ItemMedia>
                            )}
                            <ItemContent>
                              <ItemTitle>{li.name}</ItemTitle>
                              <ItemDescription>
                                <span className="inline-flex items-center gap-3 flex-wrap mt-0.5">
                                  {li.color && (
                                    <span className="inline-flex items-center gap-1">
                                      {t('dashboard.lineItem.color')}
                                      <span
                                        className="w-3 h-3 rounded-full border border-gray-300 inline-block"
                                        style={{ backgroundColor: li.color }}
                                      />
                                    </span>
                                  )}
                                  {li.size && (
                                    <span className="inline-flex items-center gap-1">
                                      {t('dashboard.lineItem.size')}
                                      <span
                                        className="text-[12px] px-1.5 border rounded font-medium text-[#111827]"
                                        style={{ borderColor: '#E5E5E5' }}
                                      >
                                        {li.size}
                                      </span>
                                    </span>
                                  )}
                                  {li.quantity > 1 && (
                                    <span className="inline-flex items-center gap-1">
                                      {t('dashboard.lineItem.qty')}{' '}
                                      <span className="primary-font-medium" style={{ color: txt1 }}>
                                        {li.quantity}
                                      </span>
                                    </span>
                                  )}
                                </span>
                              </ItemDescription>
                            </ItemContent>
                            <ItemContent className="flex-none text-right">
                              <ItemTitle>
                                {fmtAmount(li.price * li.quantity, li.currency)}
                              </ItemTitle>
                            </ItemContent>
                          </Item>
                        </ItemGroup>
                      )}

                      {typeof sub.shipping === 'number' && sub.shipping > 0 && (
                        <div className="flex justify-end items-center gap-2">
                          <span className="text-[13px]" style={{ color: txt2 }}>
                            {t('dashboard.upcoming.shipping')}
                          </span>
                          <span className="text-[13px]" style={{ color: txt1 }}>
                            {fmtAmount(sub.shipping, sub.currency)}
                          </span>
                        </div>
                      )}

                      {retention && (
                        <div className="flex justify-end items-center gap-2">
                          <span className="text-[13px]" style={{ color: txt2 }}>
                            {t('dashboard.upcoming.discount', {
                              percent: retention.terms.percentage,
                            })}
                          </span>
                          <span className="text-[13px]" style={{ color: '#15803D' }}>
                            −
                            {fmtAmount(
                              retention.fullCents - retention.discountedCents,
                              sub.currency,
                            )}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-end items-center gap-2 pt-3">
                        <span className="text-[14px]" style={{ color: txt2 }}>
                          {t('dashboard.upcoming.total')}
                        </span>
                        <span className="primary-font-medium text-[20px]" style={{ color: txt1 }}>
                          {fmtAmount(retention?.discountedCents ?? sub.amount, sub.currency)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <h2 className="primary-font-semibold text-[20px] mb-1" style={{ color: txt1 }}>
              {t('orders.title')}
            </h2>
            <p className="text-[15px] mb-4" style={{ color: txt2 }}>
              {t('orders.subtitle')}
            </p>

            <div className="mb-8">
              {ordersLoading ? (
                <div className="flex justify-center items-center py-10">
                  <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin" />
                </div>
              ) : ordersError ? (
                <div className="text-center py-10 text-[14px] text-[#DC2626]">{ordersError}</div>
              ) : orders.length === 0 ? (
                <div className="text-center py-10 text-[14px]" style={{ color: txt2 }}>
                  {t('orders.empty')}
                </div>
              ) : (
                <>
                  <div className="sm:hidden flex flex-col gap-2">
                    {orders.map((order) => {
                      const cfg = getOrderStatusCfg(order.status);
                      return (
                        <button
                          key={order.id}
                          onClick={() => openOrderDetail(order.id)}
                          className="w-full text-left rounded-[10px] bg-white border border-[#E5E5E5] p-4 hover:bg-[#f9fafb] transition-colors"
                        >
                          <div className="flex justify-between items-start gap-2 mb-2">
                            <span className="font-medium text-[14px] text-[#111827] truncate">
                              #{order.order_number}
                            </span>
                            <span className="inline-flex bg-white border border-[#E5E5E5] rounded-full px-2 py-0.5 items-center gap-1.5 text-[12px] flex-shrink-0">
                              {cfg.spinner ? (
                                <img
                                  src="/assets/images/icons/pending.png"
                                  alt=""
                                  className="w-3 h-3"
                                />
                              ) : (
                                <span
                                  className="w-2 h-2 rounded-full inline-block flex-shrink-0"
                                  style={{ backgroundColor: cfg.dot }}
                                />
                              )}
                              {fmtOrderStatus(order.status)}
                            </span>
                          </div>
                          <div className="flex justify-between items-end">
                            <span className="text-[12px] text-[#6B7280]">
                              {fmtDate(order.created_at)}
                            </span>
                            <span className="text-[14px] font-medium text-[#111827]">
                              {fmtAmount(order.total_price, order.currency || 'usd')}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-2 text-[13px] text-[#6B7280]">
                        <button
                          onClick={() => changeOrdersPage(Math.max(1, page - 1))}
                          disabled={page === 1}
                          className="px-3 py-1.5 rounded-[6px] border border-[#E5E5E5] bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {t('common.previous')}
                        </button>
                        <span>{t('common.pageOf', { current: page, total: totalPages })}</span>
                        <button
                          onClick={() => changeOrdersPage(Math.min(totalPages, page + 1))}
                          disabled={page >= totalPages}
                          className="px-3 py-1.5 rounded-[6px] border border-[#E5E5E5] bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {t('common.next')}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="hidden sm:block">
                    <DataTable
                      columns={orderTableColumns}
                      rows={orderTableRows}
                      checkbox={false}
                      onRowClick={(row) => openOrderDetail(row.id)}
                      externalPagination={true}
                      page={page}
                      totalPages={totalPages}
                      totalCount={ordersTotal}
                      rowsPerPage={ordersPerPage}
                      onPageChange={(p) => changeOrdersPage(p)}
                      onRowsPerPageChange={() => {}}
                    />
                  </div>
                </>
              )}
            </div>

            <h2 className="primary-font-semibold text-[20px] mb-1" style={{ color: txt1 }}>
              {t('account.title')}
            </h2>
            <p className="text-[14px] mb-3" style={{ color: txt2 }}>
              {t('account.subtitle')}
            </p>

            <div
              className="rounded-[10px] p-4 mb-8"
              style={{
                border: '1px solid #E5E5E5',
                backgroundColor: '#ffffff',
                boxShadow: '0px 1px 3px 0px #0000001A',
              }}
            >
              {accountLoading ? (
                <div className="flex justify-center items-center py-4">
                  <div className="w-7 h-7 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin" />
                </div>
              ) : accountError ? (
                <div className="text-center py-4 text-[13px] text-[#DC2626]">{accountError}</div>
              ) : account ? (
                <>
                  <h3 className="primary-font-medium text-[17px] mb-0.5" style={{ color: txt1 }}>
                    {account.name?.trim() || account.email}
                  </h3>
                  <p className="text-[13px] mb-3" style={{ color: '#156BE7' }}>
                    {account.email}
                  </p>

                  <div>
                    <p className="text-[14px] font-medium mb-2" style={{ color: txt1 }}>
                      {t('account.shippingAddress')}
                    </p>
                    {account.shipping_address ? (
                      <div
                        className="rounded-[8px] bg-[#FAFAFA] px-3 py-2 flex flex-col gap-0.5"
                        style={{ border: '1px solid #e5e7eb' }}
                      >
                        {formatAddressLines(account.shipping_address).map((line, i) => (
                          <p key={i} className="text-[12px]" style={{ color: txt1 }}>
                            {line}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <div
                        className="rounded-[8px] bg-[#FAFAFA] px-3 h-[52px] flex items-center"
                        style={{ border: '1px solid #e5e7eb' }}
                      >
                        <p className="text-[12px]" style={{ color: txt2 }}>
                          {t('account.noShippingAddress')}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
            <PortalBannerImage banner={content?.bottomBanner ?? null} position="bottom" />
          </>
        )}
      </div>

      <Dialog open={!!detailOrderId} onOpenChange={(o) => !o && closeOrderDetail()}>
        <DialogContent
          className="max-w-[640px] p-0 overflow-hidden rounded-[12px]"
          hideClose
          container={portalContainer}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5]">
            <span className="text-[15px] primary-font-medium text-[#111827]">
              {detailOrder
                ? t('orders.detail.title', { number: detailOrder.order_number })
                : t('orders.detail.titleFallback')}
            </span>
            {detailOrder &&
              (() => {
                const cfg = getOrderStatusCfg(detailOrder.status);
                return (
                  <span className="inline-flex bg-white border border-[#E5E5E5] rounded-full px-2 items-center gap-1.5 text-[13px]">
                    {cfg.spinner ? (
                      <img src="/assets/images/icons/pending.png" alt="" className="w-3 h-3" />
                    ) : (
                      <span
                        className="w-2 h-2 rounded-full inline-block flex-shrink-0"
                        style={{ backgroundColor: cfg.dot }}
                      />
                    )}
                    {fmtOrderStatus(detailOrder.status)}
                  </span>
                );
              })()}
          </div>

          {detailLoading ? (
            <div className="flex justify-center items-center py-10">
              <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin" />
            </div>
          ) : detailError ? (
            <div className="text-center py-10 text-[14px] text-[#DC2626]">{detailError}</div>
          ) : detailOrder ? (
            <div className="px-5 py-4 flex flex-col gap-4">
              <div className="flex items-center gap-2 text-[13px]" style={{ color: txt2 }}>
                <span className="font-medium" style={{ color: txt1 }}>
                  {t('orders.detail.placed')}
                </span>
                <span>{fmtDateTime(detailOrder.created_at)}</span>
                {detailOrder.is_recurring && (
                  <span className="inline-flex items-center gap-1 ml-2">
                    <RefreshCw size={13} color="#6B7280" />
                    <span>{t('orders.detail.recurring')}</span>
                  </span>
                )}
              </div>

              {detailOrder.line_items.length > 0 && (
                <ItemGroup className="gap-2">
                  {detailOrder.line_items.map((li) => (
                    <Item key={li.id} variant="outline" className="items-start py-3">
                      {li.image && (
                        <ItemMedia variant="image" className="mt-0.5">
                          <img
                            src={li.image}
                            alt={li.name}
                            className="w-full h-full object-cover"
                          />
                        </ItemMedia>
                      )}
                      <ItemContent>
                        <ItemTitle className="text-[13px]">{li.name}</ItemTitle>
                        <ItemDescription>
                          <span className="inline-flex items-center gap-3 flex-wrap mt-1">
                            {li.color && (
                              <span className="inline-flex items-center gap-1">
                                {t('dashboard.lineItem.color')}
                                <span
                                  className="w-3.5 h-3.5 rounded-full border border-gray-300 inline-block"
                                  style={{ backgroundColor: li.color }}
                                />
                              </span>
                            )}
                            {li.size && (
                              <span className="inline-flex items-center gap-1.5">
                                {t('dashboard.lineItem.size')}
                                <span
                                  className="text-[12px] px-2 py-0 border rounded-[4px] font-semibold text-[#111827] bg-white"
                                  style={{ borderColor: '#E5E5E5' }}
                                >
                                  {li.size}
                                </span>
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1">
                              {t('dashboard.lineItem.qty')}{' '}
                              <span className="primary-font-medium" style={{ color: txt1 }}>
                                {li.quantity}
                              </span>
                            </span>
                          </span>
                        </ItemDescription>
                      </ItemContent>
                      <ItemContent className="flex-none text-right">
                        <ItemTitle className="text-[14px]">
                          {fmtAmount(li.price * li.quantity, detailOrder.currency || 'usd')}
                        </ItemTitle>
                      </ItemContent>
                    </Item>
                  ))}
                </ItemGroup>
              )}

              <div className="flex flex-col gap-1.5 text-[13px]" style={{ color: txt2 }}>
                <div className="flex justify-between">
                  <span>{t('orders.detail.subtotal')}</span>
                  <span style={{ color: txt1 }}>
                    {fmtAmount(detailOrder.subtotal, detailOrder.currency || 'usd')}
                  </span>
                </div>
                {detailOrder.discount > 0 && (
                  <div className="flex justify-between">
                    <span>{t('orders.detail.discount')}</span>
                    <span style={{ color: txt1 }}>
                      -{fmtAmount(detailOrder.discount, detailOrder.currency || 'usd')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>{t('orders.detail.shipping')}</span>
                  <span style={{ color: txt1 }}>
                    {fmtAmount(detailOrder.shipping, detailOrder.currency || 'usd')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t('orders.detail.tax')}</span>
                  <span style={{ color: txt1 }}>
                    {fmtAmount(detailOrder.tax, detailOrder.currency || 'usd')}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-[#E5E5E5] text-[14px]">
                  <span className="font-medium" style={{ color: txt1 }}>
                    {t('orders.detail.total')}
                  </span>
                  <span className="primary-font-medium text-[16px]" style={{ color: txt1 }}>
                    {fmtAmount(detailOrder.total, detailOrder.currency || 'usd')}
                  </span>
                </div>
              </div>

              {detailOrder.shipping_address && (
                <div>
                  <p className="text-[13px] font-medium mb-1.5" style={{ color: txt1 }}>
                    {t('orders.detail.shippingAddress')}
                  </p>
                  <div
                    className="rounded-[8px] bg-[#FAFAFA] px-3 py-2 text-[13px] flex flex-col gap-0.5"
                    style={{ border: '1px solid #e5e7eb', color: txt1 }}
                  >
                    {formatAddressLines(detailOrder.shipping_address).map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <div className="flex">
            <button
              onClick={closeOrderDetail}
              className="flex-1 py-3.5 text-[14px] font-medium text-[#374151] bg-[#F5F5F5] border-t border-[#E5E5E5] hover:bg-gray-50 transition-colors rounded-b-[12px]"
            >
              {t('common.close')}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!retentionSub} onOpenChange={(o) => !o && closeRetention()}>
        <DialogContent
          className="w-[calc(100%-2rem)] max-w-[420px] max-h-[calc(100dvh-2rem)] p-0 overflow-x-hidden overflow-y-auto rounded-[16px]"
          container={portalContainer}
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">{t('account.cancelSubscription')}</DialogTitle>
          {currentRetentionPage === 'before_you_go' && retentionOffer?.before_you_go && (
            <div style={{ backgroundColor: bygStyle.bg }}>
              {bygImages.bannerUrl && (
                <RetentionBanner
                  src={bygImages.bannerUrl}
                  fit={bygImages.bannerFit}
                  size={bygImages.bannerSize}
                />
              )}
              {retentionStep && retentionStep.total > 1 && bygStyle.showStep && (
                <div className="px-6 pt-6 text-[12px]" style={{ color: bygStyle.step }}>
                  {t('retention.step', {
                    current: retentionStep.current,
                    total: retentionStep.total,
                  })}
                </div>
              )}
              {bygStyle.cardPosition === 'above_headline' && bygStyle.showSubLabel && (
                <RetentionProductCard sub={retentionSub} rs={bygStyle} />
              )}
              <div className="px-6 pt-4 pb-2">
                <h2
                  className="text-[18px] primary-font-semibold mb-1"
                  style={{ color: bygStyle.headline, textAlign: bygStyle.headlineAlign }}
                >
                  {retentionOffer.before_you_go.headline}
                </h2>
                {retentionOffer.before_you_go.body && bygStyle.showBody && (
                  <p
                    className="text-[13px]"
                    style={{ color: bygStyle.subtext, textAlign: bygStyle.bodyAlign }}
                  >
                    {retentionOffer.before_you_go.body}
                  </p>
                )}
                {(retentionOffer.before_you_go.extra_text_blocks ?? []).map((block, index) => (
                  <p
                    key={index}
                    className="text-[13px] mt-2"
                    style={{
                      color: block.color ?? bygStyle.subtext,
                      textAlign: block.align ?? 'left',
                    }}
                  >
                    {block.text}
                  </p>
                ))}
              </div>
              {bygStyle.cardPosition === 'below_body' && bygStyle.showSubLabel && (
                <RetentionProductCard sub={retentionSub} rs={bygStyle} />
              )}
              {retentionOffer.before_you_go.video_url && (
                <RetentionVideo url={retentionOffer.before_you_go.video_url} />
              )}
              {bygImages.bodyUrl && (
                <RetentionInlineImage
                  src={bygImages.bodyUrl}
                  fit={bygImages.bodyFit}
                  size={bygImages.bodySize}
                />
              )}
              {/* A page order can put Before you go after the offer page: say why we moved on. */}
              {isDiscountOffer && discountUnavailable && (
                <p role="alert" className="px-6 text-[12px] text-red-600">
                  {t('retention.discount.errors.unavailable')}
                </p>
              )}
              {retentionErr && (
                <p role="alert" className="px-6 text-[12px] text-red-600">
                  {retentionErr}
                </p>
              )}
              <div className="flex gap-3 px-6 pb-6 pt-4">
                <button
                  onClick={retentionKeep}
                  disabled={retentionBusy}
                  className="flex-1 py-3 rounded-[10px] text-[14px] font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: bygStyle.secondaryBg, color: bygStyle.secondaryTxt }}
                >
                  {t('retention.keep')}
                </button>
                {/* Only when a skipped change-frequency page after it leaves Before you go as
                    the last page, Continue cancels and reads red "Continue to cancel" like the
                    Reason page. Last for any other reason (pause-only, Before-you-go-only):
                    today's neutral Continue. */}
                <button
                  onClick={retentionNext}
                  disabled={retentionBusy || bygCta === 'checking'}
                  className="flex-1 py-3 rounded-[10px] text-[14px] font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{
                    backgroundColor: bygLastStepCancel ? bygStyle.cancelBg : bygStyle.primaryBg,
                    color: bygLastStepCancel ? bygStyle.cancelTxt : bygStyle.primaryTxt,
                  }}
                >
                  {retentionBusy && bygLastStepCancel
                    ? t('common.pleaseWait')
                    : bygCta === 'checking'
                      ? t('retention.checking')
                      : bygLastStepCancel
                        ? t('retention.continueToCancel')
                        : t('common.continue')}
                </button>
              </div>
            </div>
          )}

          {retentionOffer && currentRetentionPage === null && (
            // A change-frequency page comes first and its eligibility check is running.
            <div
              role="status"
              className="px-6 py-10 text-center text-[14px]"
              style={{ backgroundColor: offerStyle.bg, color: offerStyle.subtext }}
            >
              {t('retention.checking')}
            </div>
          )}

          {currentRetentionPage === 'offer' && retentionOfferPage && frequencyChanged && (
            <div style={{ backgroundColor: offerStyle.bg }}>
              <div className="px-6 pt-6 pb-2">
                <h2
                  ref={offerResultHeadingRef}
                  tabIndex={-1}
                  className="text-[18px] primary-font-semibold mb-1 outline-none"
                  style={{ color: offerStyle.headline, textAlign: offerStyle.headlineAlign }}
                >
                  {t('retention.frequency.successTitle')}
                </h2>
                <p className="text-[13px] mt-3" style={{ color: offerStyle.headline }}>
                  {t('retention.frequency.newSchedule', {
                    frequency: formatFrequency(
                      t,
                      frequencyChanged.frequency_value,
                      frequencyChanged.frequency_unit,
                    ),
                  })}
                </p>
                <p className="text-[13px] mt-1" style={{ color: offerStyle.subtext }}>
                  {t('retention.frequency.nextOrderOn', {
                    date: fmtDate(frequencyChanged.next_payment_date),
                  })}
                </p>
              </div>
              <div className="flex flex-col gap-3 px-6 pb-6 pt-4">
                <button
                  onClick={closeRetention}
                  className="py-3 rounded-[10px] text-[14px] font-medium hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: offerStyle.primaryBg, color: offerStyle.primaryTxt }}
                >
                  {t('retention.frequency.done')}
                </button>
              </div>
            </div>
          )}

          {currentRetentionPage === 'offer' && retentionOfferPage && discountApplied && (
            <div style={{ backgroundColor: offerStyle.bg }}>
              <div className="px-6 pt-6 pb-2">
                <h2
                  ref={offerResultHeadingRef}
                  tabIndex={-1}
                  className="text-[18px] primary-font-semibold mb-1 outline-none"
                  style={{ color: offerStyle.headline, textAlign: offerStyle.headlineAlign }}
                >
                  {t('retention.discount.successTitle', {
                    percent: discountApplied.percentage,
                    duration: formatDiscountDuration(t, discountApplied),
                  })}
                </h2>
              </div>
              <div className="flex flex-col gap-3 px-6 pb-6 pt-4">
                <button
                  onClick={closeRetention}
                  className="py-3 rounded-[10px] text-[14px] font-medium hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: offerStyle.primaryBg, color: offerStyle.primaryTxt }}
                >
                  {t('retention.discount.done')}
                </button>
              </div>
            </div>
          )}

          {currentRetentionPage === 'offer' && retentionOfferPage && frequencyPending && (
            // The change may have been applied; never cancel unless they click "Cancel anyway".
            <div style={{ backgroundColor: offerStyle.bg }}>
              <div className="px-6 pt-6 pb-2">
                <h2
                  ref={offerResultHeadingRef}
                  tabIndex={-1}
                  className="text-[18px] primary-font-semibold mb-1 outline-none"
                  style={{ color: offerStyle.headline, textAlign: offerStyle.headlineAlign }}
                >
                  {t('retention.frequency.pendingTitle')}
                </h2>
                <p
                  className="text-[13px]"
                  style={{ color: offerStyle.subtext, textAlign: offerStyle.bodyAlign }}
                >
                  {t('retention.frequency.pendingBody')}
                </p>
              </div>
              {retentionErr && (
                <p role="alert" className="px-6 text-[12px] text-red-600">
                  {retentionErr}
                </p>
              )}
              <div className="flex flex-col gap-3 px-6 pb-6 pt-4">
                <button
                  onClick={closeRetention}
                  disabled={retentionBusy}
                  className="py-3 rounded-[10px] text-[14px] font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: offerStyle.primaryBg, color: offerStyle.primaryTxt }}
                >
                  {t('retention.frequency.done')}
                </button>
                <button
                  onClick={retentionProceedToCancel}
                  disabled={retentionBusy}
                  className="py-3 rounded-[10px] text-[14px] font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: offerStyle.cancelBg, color: offerStyle.cancelTxt }}
                >
                  {retentionBusy ? t('common.pleaseWait') : t('retention.frequency.cancelAnyway')}
                </button>
              </div>
            </div>
          )}

          {currentRetentionPage === 'offer' && retentionOfferPage && !offerResultView && (
            <div style={{ backgroundColor: offerStyle.bg }}>
              {offerImages.bannerUrl && (
                <RetentionBanner
                  src={offerImages.bannerUrl}
                  fit={offerImages.bannerFit}
                  size={offerImages.bannerSize}
                />
              )}
              {retentionStep && retentionStep.total > 1 && offerStyle.showStep && (
                <div className="px-6 pt-6 text-[12px]" style={{ color: offerStyle.step }}>
                  {t('retention.step', {
                    current: retentionStep.current,
                    total: retentionStep.total,
                  })}
                </div>
              )}
              {offerStyle.cardPosition === 'above_headline' && offerStyle.showSubLabel && (
                <RetentionProductCard sub={retentionSub} rs={offerStyle} />
              )}
              <div className="px-6 pt-4 pb-2">
                <h2
                  className="text-[18px] primary-font-semibold mb-1"
                  style={{ color: offerStyle.headline, textAlign: offerStyle.headlineAlign }}
                >
                  {offerHeadline}
                </h2>
                {offerBody && offerStyle.showBody && (
                  <p
                    className="text-[13px]"
                    style={{ color: offerStyle.subtext, textAlign: offerStyle.bodyAlign }}
                  >
                    {offerBody}
                  </p>
                )}
                {(retentionOfferPage.extra_text_blocks ?? []).map((block, index) => (
                  <p
                    key={index}
                    className="text-[13px] mt-2"
                    style={{
                      color: block.color ?? offerStyle.subtext,
                      textAlign: block.align ?? 'left',
                    }}
                  >
                    {block.text}
                  </p>
                ))}
                {isDiscountOffer && discountTerms && (
                  <p
                    className="text-[14px] font-medium mt-3"
                    style={{ color: offerStyle.headline, textAlign: offerStyle.bodyAlign }}
                  >
                    {t('retention.discount.summary', {
                      percent: discountTerms.percentage,
                      duration: formatDiscountDuration(t, discountTerms),
                    })}
                  </p>
                )}
                {isFrequencyOffer && frequencyData?.current && (
                  <p className="text-[13px] mt-3" style={{ color: offerStyle.subtext }}>
                    {t('retention.frequency.currentSchedule', {
                      frequency: formatFrequency(
                        t,
                        frequencyData.current.frequency_value,
                        frequencyData.current.frequency_unit,
                      ),
                    })}
                  </p>
                )}
                {/* After a 409 the schedules can't be accepted: hide them with the accept. */}
                {isFrequencyOffer && frequencyData && !frequencyUnavailable && (
                  <div
                    className="flex flex-col gap-2 mt-3"
                    role="radiogroup"
                    aria-label={t('retention.frequency.optionsLabel')}
                    onKeyDown={(e) => {
                      const options = frequencyData.options;
                      const current = options.findIndex(
                        (o) => o.option_id === selectedFrequency?.option_id,
                      );
                      const next = radioIndexForKey(Math.max(current, 0), e.key, options.length);
                      if (next === null || retentionBusy) return;
                      e.preventDefault();
                      setFrequencyOptionId(options[next].option_id);
                      const radios =
                        e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]');
                      radios[next]?.focus();
                    }}
                  >
                    {frequencyData.options.map((o) => {
                      const selected = selectedFrequency?.option_id === o.option_id;
                      return (
                        <button
                          key={o.option_id}
                          role="radio"
                          aria-checked={selected}
                          aria-labelledby={`frequency-option-${o.option_id}`}
                          aria-describedby={`frequency-option-${o.option_id}-date`}
                          tabIndex={selected ? 0 : -1}
                          onClick={() => setFrequencyOptionId(o.option_id)}
                          disabled={retentionBusy}
                          className="text-left px-4 py-3 rounded-[10px] text-[14px] border transition-colors disabled:opacity-50"
                          style={{
                            borderColor: selected ? offerStyle.accent : '#E5E5E5',
                            backgroundColor: selected ? 'rgba(0,0,0,0.03)' : 'transparent',
                            color: offerStyle.headline,
                          }}
                        >
                          <div id={`frequency-option-${o.option_id}`}>
                            {formatFrequency(t, o.frequency_value, o.frequency_unit)}
                          </div>
                          <div
                            id={`frequency-option-${o.option_id}-date`}
                            className="text-[12px]"
                            style={{ color: offerStyle.subtext }}
                          >
                            {t('retention.frequency.nextOrderOn', {
                              date: fmtDate(o.next_payment_date),
                            })}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {offerStyle.cardPosition === 'below_body' && offerStyle.showSubLabel && (
                <RetentionProductCard sub={retentionSub} rs={offerStyle} />
              )}
              {offerImages.bodyUrl && (
                <RetentionInlineImage
                  src={offerImages.bodyUrl}
                  fit={offerImages.bodyFit}
                  size={offerImages.bodySize}
                />
              )}
              {isFrequencyOffer && frequencyUnavailable && (
                <p role="alert" className="px-6 text-[12px] text-red-600">
                  {t('retention.frequency.errors.unavailable')}
                </p>
              )}
              {isDiscountOffer && discountUnavailable && (
                <p role="alert" className="px-6 text-[12px] text-red-600">
                  {t('retention.discount.errors.unavailable')}
                </p>
              )}
              {retentionErr && (
                <p role="alert" className="px-6 text-[12px] text-red-600">
                  {retentionErr}
                </p>
              )}
              <div className="flex flex-col gap-3 px-6 pb-6 pt-4">
                {isFrequencyOffer ? (
                  frequencyData &&
                  !frequencyUnavailable && (
                    <button
                      onClick={retentionAcceptFrequency}
                      disabled={retentionBusy || !selectedFrequency}
                      className="py-3 rounded-[10px] text-[14px] font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                      style={{
                        backgroundColor: offerStyle.primaryBg,
                        color: offerStyle.primaryTxt,
                      }}
                    >
                      {frequencyAcceptLabel(t, {
                        busy: retentionBusy,
                        err: retentionErr,
                        acceptFailed: frequencyAcceptFailed,
                        acceptLabel: retentionOfferPage.accept_label,
                      })}
                    </button>
                  )
                ) : isDiscountOffer ? (
                  // Hidden after a 409, and when the terms can't be shown.
                  discountTerms &&
                  !discountUnavailable && (
                    <button
                      onClick={retentionAcceptDiscount}
                      disabled={retentionBusy}
                      className="py-3 rounded-[10px] text-[14px] font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                      style={{
                        backgroundColor: offerStyle.primaryBg,
                        color: offerStyle.primaryTxt,
                      }}
                    >
                      {discountAcceptLabel(t, {
                        busy: retentionBusy,
                        unconfirmed: discountUnconfirmed,
                        acceptLabel: retentionOfferPage.accept_label,
                        percentage: discountTerms.percentage,
                      })}
                    </button>
                  )
                ) : (
                  <button
                    onClick={retentionAcceptPause}
                    disabled={retentionBusy}
                    className="py-3 rounded-[10px] text-[14px] font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: offerStyle.primaryBg, color: offerStyle.primaryTxt }}
                  >
                    {retentionBusy
                      ? t('common.pleaseWait')
                      : retentionOfferPage.accept_label ||
                        t('retention.pauseForCycles', {
                          count: retentionOfferPage.pause_cycles,
                        })}
                  </button>
                )}
                <button
                  onClick={retentionKeep}
                  disabled={retentionBusy}
                  className="py-3 rounded-[10px] text-[14px] font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{
                    backgroundColor: offerStyle.secondaryBg,
                    color: offerStyle.secondaryTxt,
                  }}
                >
                  {t('retention.keep')}
                </button>
                <button
                  onClick={retentionNext}
                  disabled={retentionBusy}
                  className="py-3 rounded-[10px] text-[14px] font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{
                    backgroundColor: offerStyle.secondaryBg,
                    color: offerStyle.secondaryTxt,
                  }}
                >
                  {retentionOfferPage.decline_label || t('retention.declineDefault')}
                </button>
                {retentionPreviousPage && (
                  <button
                    onClick={retentionBack}
                    disabled={retentionBusy}
                    className="py-3 rounded-[10px] text-[14px] font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{
                      backgroundColor: offerStyle.secondaryBg,
                      color: offerStyle.secondaryTxt,
                    }}
                  >
                    {t('common.back')}
                  </button>
                )}
              </div>
            </div>
          )}

          {currentRetentionPage === 'cancellation_reason' &&
            retentionOffer?.cancellation_reason && (
              <div style={{ backgroundColor: reasonStyle.bg }}>
                {reasonImages.bannerUrl && (
                  <RetentionBanner
                    src={reasonImages.bannerUrl}
                    fit={reasonImages.bannerFit}
                    size={reasonImages.bannerSize}
                  />
                )}
                {retentionStep && retentionStep.total > 1 && reasonStyle.showStep && (
                  <div className="px-6 pt-6 text-[12px]" style={{ color: reasonStyle.step }}>
                    {t('retention.step', {
                      current: retentionStep.current,
                      total: retentionStep.total,
                    })}
                  </div>
                )}
                {reasonStyle.cardPosition === 'above_headline' && reasonStyle.showSubLabel && (
                  <RetentionProductCard sub={retentionSub} rs={reasonStyle} />
                )}
                <div className="px-6 pt-4 pb-2">
                  {/* The discount offer was declined by the backend (409): say why we moved on. */}
                  {isDiscountOffer && discountUnavailable && (
                    <p role="alert" className="mb-3 text-[12px] text-red-600">
                      {t('retention.discount.errors.unavailable')}
                    </p>
                  )}
                  <h2
                    className="text-[18px] primary-font-semibold mb-3"
                    style={{ color: reasonStyle.headline, textAlign: reasonStyle.headlineAlign }}
                  >
                    {retentionOffer.cancellation_reason.question}
                  </h2>
                  <div className="flex flex-col gap-2">
                    {retentionOffer.cancellation_reason.reasons.map((reason) => {
                      const selected = retentionReason === reason;
                      return (
                        <button
                          key={reason}
                          onClick={() => setRetentionReason(reason)}
                          className="text-left px-4 py-3 rounded-[10px] text-[14px] border transition-colors"
                          style={{
                            borderColor: selected ? reasonStyle.accent : '#E5E5E5',
                            backgroundColor: selected ? 'rgba(0,0,0,0.03)' : 'transparent',
                            color: reasonStyle.headline,
                          }}
                        >
                          {reason}
                        </button>
                      );
                    })}
                  </div>
                  {(retentionOffer.cancellation_reason.extra_text_blocks ?? []).map(
                    (block, index) => (
                      <p
                        key={index}
                        className="text-[13px] mt-2"
                        style={{
                          color: block.color ?? reasonStyle.subtext,
                          textAlign: block.align ?? 'left',
                        }}
                      >
                        {block.text}
                      </p>
                    ),
                  )}
                </div>
                {reasonStyle.cardPosition === 'below_body' && reasonStyle.showSubLabel && (
                  <RetentionProductCard sub={retentionSub} rs={reasonStyle} />
                )}
                {reasonImages.bodyUrl && (
                  <RetentionInlineImage
                    src={reasonImages.bodyUrl}
                    fit={reasonImages.bodyFit}
                    size={reasonImages.bodySize}
                  />
                )}
                {retentionErr && (
                  <p role="alert" className="px-6 text-[12px] text-red-600">
                    {retentionErr}
                  </p>
                )}
                <div className="flex gap-3 px-6 pb-6 pt-4">
                  {retentionPreviousPage && (
                    <button
                      onClick={retentionBack}
                      disabled={retentionBusy}
                      className="px-4 py-3 rounded-[10px] text-[14px] font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                      style={{
                        backgroundColor: reasonStyle.secondaryBg,
                        color: reasonStyle.secondaryTxt,
                      }}
                    >
                      {t('common.back')}
                    </button>
                  )}
                  {retentionOffer.cancellation_reason.allow_keep_option && (
                    <button
                      onClick={retentionKeep}
                      disabled={retentionBusy}
                      className="flex-1 py-3 rounded-[10px] text-[14px] font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                      style={{
                        backgroundColor: reasonStyle.secondaryBg,
                        color: reasonStyle.secondaryTxt,
                      }}
                    >
                      {t('retention.dontCancel')}
                    </button>
                  )}
                  {/* "Continue" when another page follows; red "Continue to cancel" when this is
                    the last page, including when the chosen reason goes to cancel. */}
                  <button
                    onClick={retentionNext}
                    disabled={retentionBusy || !retentionReason || reasonCta === 'checking'}
                    className="flex-1 py-3 rounded-[10px] text-[14px] font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                    style={{
                      backgroundColor:
                        reasonCta === 'cancel' ? reasonStyle.cancelBg : reasonStyle.primaryBg,
                      color:
                        reasonCta === 'cancel' ? reasonStyle.cancelTxt : reasonStyle.primaryTxt,
                    }}
                  >
                    {retentionBusy
                      ? t('common.pleaseWait')
                      : reasonCta === 'checking'
                        ? t('retention.checking')
                        : reasonCta === 'cancel'
                          ? t('retention.continueToCancel')
                          : t('common.continue')}
                  </button>
                </div>
              </div>
            )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelSub} onOpenChange={(o) => !o && closeCancel()}>
        <DialogContent
          className="max-w-[420px] p-0 overflow-hidden rounded-[16px]"
          hideClose
          container={portalContainer}
        >
          <div className="px-6 pt-6 pb-2">
            <p className="text-[12px] text-[#6B7280] mb-2">{subLabel(cancelSub)}</p>
            <h2 className="text-[18px] primary-font-semibold text-[#111827] mb-1">
              {t('cancel.title')}
            </h2>
            <p className="text-[13px] text-[#6B7280]">
              {t('cancel.body', { date: fmtDate(cancelSub?.next_payment_date) })}
            </p>
            {cancelErr && <p className="text-[12px] text-red-600 mt-3">{cancelErr}</p>}
          </div>
          <div className="flex gap-3 px-6 pb-6 pt-4">
            <button
              onClick={closeCancel}
              disabled={canceling}
              className="flex-1 py-3 rounded-[10px] text-[14px] font-medium text-[#374151] bg-[#F5F5F5] hover:bg-[#EBEBEB] transition-colors disabled:opacity-50"
            >
              {t('cancel.keep')}
            </button>
            <button
              onClick={submitCancel}
              disabled={canceling}
              className="flex-1 py-3 rounded-[10px] text-[14px] font-medium text-white hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ backgroundColor: '#E15C5C' }}
            >
              {canceling ? t('cancel.canceling') : t('cancel.confirm')}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPortalDashboard;
